import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { localDate } from "@faire/web/common/localization/localDate";
import {
  StrictLocalizeFunction,
  backwardsCompatibleValues,
} from "@faire/web/common/localization/localize";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { differenceInCalendarYears } from "date-fns/differenceInCalendarYears";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { getHours } from "date-fns/getHours";

import { ConversationModel } from "./__internal__/ConversationModel";
import {
  MessageModel,
  getCreatedAt as getMessageModelCreatedAt,
} from "./__internal__/MessageModel";

export const isConversationToken = (token: string): boolean => {
  return token.startsWith("mc_");
};

export const toReadableLatestMessageDate = (
  unixDate: number,
  strictLocalize: StrictLocalizeFunction
) => {
  const diffDays = differenceInCalendarDays(Date.now(), unixDate);

  if (!unixDate || diffDays < 0) {
    return "";
  } else if (diffDays === 0) {
    return localDate(unixDate, "p");
  } else if (diffDays < 7) {
    return strictLocalize(
      {
        id: "UOh5gz",
        defaultMessage: "{days, plural, one{# day ago} other{# days ago}}",
        description: {
          text: "Number of days since the message was sent",
        },
      },
      backwardsCompatibleValues({ days: diffDays })
    );
  } else if (differenceInCalendarYears(Date.now(), unixDate) > 0) {
    return localDate(unixDate, "PP");
  } else {
    return localDate(unixDate, "MMM d");
  }
};

export const getOtherPartyCompanyName = (
  conversation: ConversationModel,
  strictLocalize: StrictLocalizeFunction
) => {
  let companyName = "";
  if (conversation.otherParty?.type === IUser.Type.ADMIN) {
    companyName = strictLocalize({
      id: "feVZJ0",
      defaultMessage: "Faire Support",
      description: {
        text: "Name of the other party when chatting with Faire Support",
      },
    });
  } else if (
    conversation.otherParty?.type === IUser.Type.USER &&
    conversation.retailer &&
    conversation.retailer.name
  ) {
    companyName = conversation.retailer.name;
  } else if (
    conversation.otherParty?.type === IUser.Type.BRAND_USER &&
    conversation.brand &&
    conversation.brand.name
  ) {
    companyName = conversation.brand.name;
  } else if (
    conversation.otherParty?.type === IUser.Type.GUEST &&
    conversation.otherPartyCompany
  ) {
    companyName = conversation.otherPartyCompany;
  }
  return companyName;
};

export const shouldShowHourTimestamp = (
  currentMessage: MessageModel,
  previousMessage?: MessageModel
): boolean => {
  const currentMessageCreatedAt = getMessageModelCreatedAt(currentMessage);
  const previousMessageCreatedAt = previousMessage
    ? getMessageModelCreatedAt(previousMessage)
    : undefined;
  if (!currentMessageCreatedAt) {
    return false;
  }
  // FIXME: Should we also hide these hour timestamps on synthetic messages?

  if (
    !previousMessage ||
    !previousMessageCreatedAt ||
    previousMessage.authorToken !== currentMessage.authorToken
  ) {
    return true;
  }

  const differenceInTimeMinutes = differenceInMinutes(
    currentMessageCreatedAt,
    previousMessageCreatedAt
  );

  return differenceInTimeMinutes >= 1;
};

export const shouldShowDateTimestamp = (
  currentMessage: MessageModel,
  nextMessage?: MessageModel
): boolean => {
  const currentMessageCreatedAt = getMessageModelCreatedAt(currentMessage);
  const nextMessageCreatedAt = nextMessage
    ? getMessageModelCreatedAt(nextMessage)
    : undefined;
  if (!currentMessageCreatedAt) {
    return false;
  }

  if (!nextMessage || !nextMessageCreatedAt) {
    return true;
  }

  const diffHours = differenceInHours(
    currentMessageCreatedAt,
    nextMessageCreatedAt
  );
  const currentHours = getHours(currentMessageCreatedAt);

  return Math.floor(diffHours / currentHours) > 0;
};

export const shouldShowTimestampOnDeliveredMessage = (
  currentMessage: MessageModel,
  nextMessage?: MessageModel
): boolean => {
  const currentMessageCreatedAt = getMessageModelCreatedAt(currentMessage);
  const nextMessageCreatedAt = nextMessage
    ? getMessageModelCreatedAt(nextMessage)
    : undefined;
  if (!currentMessageCreatedAt) {
    return false;
  }

  if (
    !nextMessage ||
    !nextMessageCreatedAt ||
    nextMessage?.authorToken !== currentMessage.authorToken
  ) {
    return true;
  }

  const differenceInTimeMinutes = differenceInMinutes(
    nextMessageCreatedAt,
    currentMessageCreatedAt
  );

  return differenceInTimeMinutes >= 1;
};

export const isOnVacation = (
  brand?: IGetMessengerConversationsResponse.ISimpleBrand
): boolean => {
  const now = Date.now();

  return (
    !!brand &&
    !!brand.vacation_start_date &&
    !!brand.vacation_end_date &&
    brand.vacation_start_date <= now &&
    brand.vacation_end_date >= now
  );
};

export const getBrandTokenInSession = (): string | undefined => {
  return getGlobalProperty("brandToken");
};
